/**
 * Category profile — maps a product (with optional matched driver) to:
 *   - kind            : 'measure' | 'smart_plug' | 'installer' | 'monitoring' | 'other'
 *   - microcopy       : single short reassurance line (≤90 chars)
 *   - warning         : optional "first measure / first analyse" line for
 *                       large hardware (heat pump / battery / EV / inverter)
 *   - primaryLabel    : the primary CTA label
 *   - secondaryLabel  : secondary CTA label (Plan later / Niet relevant / etc)
 *   - inquiryLabel    : tertiary (Installatiehulp nodig?)
 *
 * Order of resolution: product.component_type wins, then product.category,
 * then driver.component_type, then keyword sniff on product.name.
 */

const INSTALLER_TYPES = new Set([
  'heat_pump', 'home_battery', 'ev_charger', 'fuse_box_3_phase',
  'insulation', 'ventilation', 'glass', 'boiler', 'boiler_relay',
  'inverter', 'inverter_control', 'solar_panels',
]);

const MEASURE_TYPES = new Set([
  'p1_meter', 'energy_meter', 'smart_meter', 'measure_first',
]);

const SMART_PLUG_TYPES = new Set([
  'smart_plug', 'smart_socket', 'plug',
]);

function detectKind(product, driver) {
  if (!product) return 'other';
  const ct = product.component_type || driver?.component_type || '';
  const cat = product.category || '';
  const name = (product.name || '').toLowerCase();

  // Monitoring subscription — first match
  if (product.product_id === 'powersure_monitoring' ||
      ct === 'monitoring' || cat === 'monitoring' ||
      /maandelijkse monitoring|monitoring/.test(name)) {
    return 'monitoring';
  }
  if (INSTALLER_TYPES.has(ct)) return 'installer';
  if (MEASURE_TYPES.has(ct)) return 'measure';
  if (SMART_PLUG_TYPES.has(ct)) return 'smart_plug';
  if (cat === 'measure_first' || /p1[\s-]?meter|p1\sdongle|slimme.?meter/.test(name)) return 'measure';
  if (/stekker|plug|tussenstekker/.test(name)) return 'smart_plug';
  if (cat === 'installer' || /laadpaal|wallbox|warmtepomp|thuisbatterij|home.?battery|isolatie|glas|ventilatie/.test(name)) {
    return 'installer';
  }
  return 'other';
}

function warningFor(ct, name) {
  const n = (name || '').toLowerCase();
  if (ct === 'home_battery' || /thuisbatterij|home.?battery/.test(n))
    return 'Koop geen thuisbatterij zonder verbruiksanalyse. Vraag eerst advies.';
  if (ct === 'heat_pump' || /warmtepomp|heat.?pump/.test(n))
    return 'Controleer eerst of uw woning en verbruik bij een warmtepomp passen.';
  if (ct === 'ev_charger' || /laadpaal|wallbox|ev.?lader/.test(n))
    return 'Stem laden af op zonnepanelen, piekverbruik en aansluiting.';
  if (ct === 'inverter' || /inverter|omvormer/.test(n))
    return 'Wijzigingen aan de omvormer raken het PV-systeem — laat een specialist meekijken.';
  if (ct === 'fuse_box_3_phase' || /groepenkast|3-fasen/.test(n))
    return 'Wijzigingen aan de groepenkast vereisen een installateur.';
  return null;
}

export function getCategoryProfile(product, driver) {
  const kind = detectKind(product, driver);
  const ct = product?.component_type || driver?.component_type || '';
  const name = product?.name || '';

  if (kind === 'measure') {
    return {
      kind,
      microcopy: 'Meet eerst wat er echt gebeurt. Powersure gebruikt deze data voor inzicht en advies.',
      warning: null,
      primaryLabel: 'Bekijk product',
      secondaryLabel: 'Voeg toe aan mijn plan',
      inquiryLabel: 'Installatiehulp nodig?',
    };
  }
  if (kind === 'smart_plug') {
    return {
      kind,
      microcopy: 'Kan helpen om verbruik per apparaat te meten. Schakelen alleen waar technisch veilig en na toestemming.',
      warning: null,
      primaryLabel: 'Bekijk product',
      secondaryLabel: 'Voeg toe aan mijn plan',
      inquiryLabel: 'Installatieadvies nodig?',
    };
  }
  if (kind === 'installer') {
    return {
      kind,
      microcopy: 'Dit vraagt meestal om controle of uitvoering door een specialist. Powersure helpt uw aanvraag klaarzetten.',
      warning: warningFor(ct, name),
      primaryLabel: 'Vraag offerte / installatieadvies',
      secondaryLabel: 'Plan later',
      inquiryLabel: 'Niet relevant',
    };
  }
  if (kind === 'monitoring') {
    return {
      kind,
      microcopy: null,
      warning: null,
      primaryLabel: null,         // monitoring uses its own subscription CTA
      secondaryLabel: null,
      inquiryLabel: null,
    };
  }
  // Other / unknown — keep current generic behaviour
  return {
    kind: 'other',
    microcopy: null,
    warning: null,
    primaryLabel: 'Bekijk als mogelijke optie',
    secondaryLabel: null,
    inquiryLabel: 'Vraag advies over deze optie',
  };
}

export default getCategoryProfile;
